// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import useAppDispatch from "@insite/client-framework/Common/Hooks/useAppDispatch";
import useOverlayBadge from "@insite/client-framework/Common/Hooks/useOverlayBadge";
import useWidgetBadges from "@insite/client-framework/Common/Hooks/useWidgetBadges";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import { createFromProduct, ProductInfo } from "@insite/client-framework/Common/ProductInfo";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import wrapInContainerStyles from "@insite/client-framework/Common/wrapInContainerStyles";
import { ProductContext, ProductContextModel } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import loadRealTimeInventory from "@insite/client-framework/Store/CommonHandlers/LoadRealTimeInventory";
import loadRealTimePricing from "@insite/client-framework/Store/CommonHandlers/LoadRealTimePricing";
import updateProductInfo from "@insite/client-framework/Store/Components/ProductInfoList/Handlers/UpdateProductInfo";
import { getProductInfoFromList } from "@insite/client-framework/Store/Components/ProductInfoList/ProductInfoListSelectors";
import sendProductRecommendationTracking from "@insite/client-framework/Store/Components/ProductRecommendations/Handlers/SendProductRecommendationTracking";
import loadVariantChildren from "@insite/client-framework/Store/Data/Products/Handlers/LoadVariantChildren";
import { getVariantChildrenDataView } from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import translate from "@insite/client-framework/Translate";
import { ProductInventoryDto, ProductModel } from "@insite/client-framework/Types/ApiModels";
import ProductAddToCartButton from "@insite/content-library/Components/ProductAddToCartButton";
import { ProductAddToListLinkStyles } from "@insite/content-library/Components/ProductAddToListLink";
import { ProductAvailabilityStyles } from "@insite/content-library/Components/ProductAvailability";
import ProductBrand, { ProductBrandStyles } from "@insite/content-library/Components/ProductBrand";
import ProductContextAvailability from "@insite/content-library/Components/ProductContextAvailability";
import ProductDescription, { ProductDescriptionStyles } from "@insite/content-library/Components/ProductDescription";
import ProductImage, { ProductImageStyles } from "@insite/content-library/Components/ProductImage";
import ProductImageBadges from "@insite/content-library/Components/ProductImageBadges";
import ProductPartNumbers, { ProductPartNumbersStyles } from "@insite/content-library/Components/ProductPartNumbers";
import ProductPrice from "@insite/content-library/Components/ProductPrice";
import ProductQuantityBreakPricing, {
    ProductQuantityBreakPricingStyles,
} from "@insite/content-library/Components/ProductQuantityBreakPricing";
import ProductTextBadges from "@insite/content-library/Components/ProductTextBadges";
import ProductUnitOfMeasureSelect from "@insite/content-library/Components/ProductUnitOfMeasureSelect";
import Badge from "@insite/mobius/Badge";
import { ButtonPresentationProps } from "@insite/mobius/Button";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import { SelectPresentationProps } from "@insite/mobius/Select";
import { TextFieldProps } from "@insite/mobius/TextField";
import { TypographyPresentationProps } from "@insite/mobius/Typography";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import SignInButton from "@keller/App/Components/Common/SignInButton";
import AddToCartVariantParent from "@keller/App/Components/Product/AddToCartVariantParent";
import ProductContextPreviousPurchase from "@keller/App/Components/Product/ProductContextPreviousPurchase";
import ProductGridLayout, { ProductGridLayoutStyle } from "@keller/App/Components/Product/ProductGridLayout";
import ProductOriginally from "@keller/App/Components/Product/ProductOriginally";
import { getIsNonPurchasable, xIsAuthenticated } from "@keller/App/Store/Selectors/GeneralSelectors";
import ProductAddToListLink from "@keller/Overrides/Components/ProductAddToListLink";
import ProductDetailsVariantDropdown from "@keller/Overrides/Widgets/ProductDetails/ProductDetailsVariantDropdown";
import ProductQuantityOrdered from "@keller/Overrides/Components/ProductQuantityOrdered";
import { color, xScreen } from "@keller/XDL/Utilities";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

// Reads the `isStocked` product property (populated from the ERP stock flag) tolerantly across
// possible casings/values so a variant child can be treated as in stock. Mirrors
// ProductListProductInformation so carousel cards select the same default item as PLP.
const isVariantChildStocked = (child: ProductModel): boolean => {
    const properties = child.properties as { [key: string]: string } | undefined;
    const raw = properties?.isStocked ?? properties?.IsStocked;
    if (raw === undefined || raw === null) {
        return false;
    }
    const value = String(raw).trim().toLowerCase();
    return value === "true" || value === "1" || value === "yes";
};

const mapStateToProps = (state: ApplicationState, ownProps: OwnProps) => {
    const productId = ownProps.product.id;
    const isVariantParent = ownProps.product.isVariantParent;

    const variantSelection = isVariantParent
        ? ((state as any).xProductListVariants?.variantSelectionsByProductId?.[productId] ?? {})
        : {};

    const variantChildrenView = getVariantChildrenDataView(state, isVariantParent ? productId : undefined);

    return {
        productInfo: getProductInfoFromList(state, ownProps.carouselId, ownProps.product.id),
        isNonPurchasable: getIsNonPurchasable(state),
        isAuthenticated: xIsAuthenticated(state),
        variantSelection,
        variantChildren: variantChildrenView.value,
        variantChildrenLoading: variantChildrenView.isLoading,
        variantPricingByProductId: (state as any).xProductListVariants?.variantPricingByProductId as
            | Record<string, any>
            | undefined,
        variantInventoryByProductId: (state as any).xProductListVariants?.variantInventoryByProductId as
            | Record<string, ProductInventoryDto>
            | undefined,
    };
};

const mapDispatchToProps = {
    updateProductInfo,
    sendProductRecommendationTracking,
    loadVariantChildren,
    loadRealTimePricing,
    loadRealTimeInventory,
};

interface OwnProps {
    carouselId: string;
    product: ProductModel;
    recommendationId?: number;
    showImage: boolean;
    showBrand: boolean;
    showTitle: boolean;
    showPartNumbers: boolean;
    showPrice: boolean;
    showAddToCart: boolean;
    showAddToList: boolean;
    showImageBadges?: boolean;
    maximumNumberOfImageBadges?: number;
    showTextBadges?: boolean;
    maximumNumberOfTextBadges?: number;
    extendedStyles?: ProductCarouselProductStyles;
    // XNG-Changes
    showInventory?: boolean;
    actionsTemplate?: (product: ProductModel) => React.ReactNode;
    showManufacturerItem?: boolean;
}

type Props = OwnProps & ResolveThunks<typeof mapDispatchToProps> & ReturnType<typeof mapStateToProps>;

export interface ProductCarouselProductStyles {
    productImageStyles?: ProductImageStyles;
    productBrandWrapper?: InjectableCss;
    productBrandStyles?: ProductBrandStyles;
    productDescriptionStyles?: ProductDescriptionStyles;
    productPartNumbersStyles?: ProductPartNumbersStyles;
    productPriceWrapper?: InjectableCss;
    productUnitOfMeasureSelectStyles?: SelectPresentationProps;
    productQuantityBreakPricingStyles?: ProductQuantityBreakPricingStyles;
    addToCartContainer?: GridContainerProps;
    qtyGridItem?: GridItemProps;
    productQuantityOrderedStyles?: TextFieldProps;
    addToCartButtonGridItem?: GridItemProps;
    productAddToCartButtonStyles?: ButtonPresentationProps;
    productAddToListLinkStyles?: ProductAddToListLinkStyles;
    badgeAreaWrapper?: InjectableCss;
    badgeTextWrapper?: InjectableCss;
    badgeImageWrapper?: InjectableCss;
    badgeText?: TypographyPresentationProps;
    badgeImageProps?: JSX.IntrinsicElements["img"];
    // XNG-Changes
    productGridLayoutStyle?: ProductGridLayoutStyle;
    productAvailabilityStyles?: ProductAvailabilityStyles;
    addToCartVariantParentNonPurchasable?: ButtonPresentationProps;
    addToCartVariantParent?: ButtonPresentationProps;
    emptyElement?: InjectableCss;
    signInButton?: ButtonPresentationProps;
}

export const productCarouselProductStyles: ProductCarouselProductStyles = {
    productBrandStyles: {
        nameLink: {
            css: css`
                margin-bottom: 8px;
            `,
        },
    },
    productImageStyles: {
        linkWrappingImage: {
            css: css`
                width: 308px;
                height: 308px;
                justify-content: center;
                margin-bottom: 12px;
                background: ${color("common.white")};

                ${xScreen("lte", "xs")} {
                    width: 280px;
                    height: 280px;
                }
            `,
        },
        img: {
            css: css`
                max-width: 100%;
                max-height: 100%;
            `,
        },
    },
    productDescriptionStyles: {
        productDetailLink: {
            css: css`
                margin-bottom: 12px;
                min-height: 34px;
            `,
        },
    },
    productAddToCartButtonStyles: {
        mergeCss: true,
        css: css`
            flex-grow: 1;
        `,
    },
    badgeAreaWrapper: {
        css: css`
            position: relative;
        `,
    },
    badgeTextWrapper: {
        css: css`
            position: absolute;
            width: 50%;
            white-space: nowrap;
            overflow: hidden;
        `,
    },
    badgeImageWrapper: {
        css: css`
            position: absolute;
            width: 25%;
            height: 25%;
        `,
    },
    badgeText: {
        weight: "bold",
        css: css`
            width: 100%;
            margin: auto;
            ${wrapInContainerStyles}
        `,
    },
    addToCartVariantParentNonPurchasable: {
        sizeVariant: "small",
        mergeCss: true,
        css: css`
            font-size: 10px;
            flex-grow: 1;
        `,
    },
    addToCartVariantParent: {
        sizeVariant: "small",
        mergeCss: true,
        css: css`
            & > span {
                font-size: 13px;
                font-weight: normal;
            }
        `,
    },
    emptyElement: {
        css: css`
            height: 34px;
        `,
    },
    signInButton: {
        mergeCss: true,
        sizeVariant: "small",
        css: css`
            flex-grow: 1;
            padding: 0 24px;
        `,
    },
};

const ProductCarouselProduct = ({
    carouselId,
    product,
    showImage,
    showBrand,
    showTitle,
    showPartNumbers,
    showPrice,
    showInventory,
    showAddToCart,
    showAddToList,
    showImageBadges,
    maximumNumberOfImageBadges,
    showTextBadges,
    maximumNumberOfTextBadges,
    updateProductInfo,
    extendedStyles,
    productInfo,
    recommendationId,
    sendProductRecommendationTracking,
    actionsTemplate,
    showManufacturerItem,
    isNonPurchasable,
    isAuthenticated,
    variantSelection,
    variantChildren,
    variantChildrenLoading,
    variantPricingByProductId,
    variantInventoryByProductId,
    loadVariantChildren,
    loadRealTimePricing,
    loadRealTimeInventory,
}: Props) => {
    const [styles] = useState(() => mergeToNew(productCarouselProductStyles, extendedStyles));
    const appDispatch = useAppDispatch();

    const updateQuantity = (qtyOrdered: number) => {
        updateProductInfo({ id: carouselId, productId: product.id, qtyOrdered });
    };

    const updateUnitOfMeasure = (unitOfMeasure: string) => {
        updateProductInfo({ id: carouselId, productId: product.id, unitOfMeasure });
    };

    // ===================================================================
    // Auto-select the first in-stock variant so carousel cards (Recently Viewed
    // Products, Cross Sells, etc.) preselect an item the same way PLP/PDP do,
    // instead of showing "Choose Options" for variant-parent products.
    // ALL HOOKS MUST BE DECLARED BEFORE ANY EARLY RETURNS (Rules of Hooks)
    // ===================================================================
    useEffect(() => {
        if (product?.isVariantParent && !variantChildren && !variantChildrenLoading) {
            loadVariantChildren({ productId: product.id });
        }
    }, [product?.id, product?.isVariantParent, variantChildren, variantChildrenLoading]);

    const manuallyChangedRef = useRef(false);
    const inStockAppliedForProductRef = useRef<string | undefined>(undefined);

    useEffect(() => {
        if (!product?.isVariantParent || !product.variantTraits) {
            return;
        }
        if (manuallyChangedRef.current) {
            return;
        }

        if (variantChildren && variantChildren.length > 0 && inStockAppliedForProductRef.current !== product.id) {
            const firstInStockChild = variantChildren.find(isVariantChildStocked) ?? variantChildren[0];
            if (firstInStockChild?.childTraitValues && firstInStockChild.childTraitValues.length > 0) {
                firstInStockChild.childTraitValues.forEach(childTraitValue => {
                    appDispatch({
                        type: "XKeller/UpdateProductListVariantSelection",
                        parentProductId: product.id,
                        variantTraitId: childTraitValue.styleTraitId,
                        traitValueId: childTraitValue.id,
                    });
                });
                inStockAppliedForProductRef.current = product.id;
                return;
            }
        }

        product.variantTraits.forEach(trait => {
            if (!variantSelection[trait.id] && trait.traitValues && trait.traitValues.length > 0) {
                const firstAvailable = trait.traitValues.find(tv => !tv.isDisabled) ?? trait.traitValues[0];
                appDispatch({
                    type: "XKeller/UpdateProductListVariantSelection",
                    parentProductId: product.id,
                    variantTraitId: trait.id,
                    traitValueId: firstAvailable.id,
                });
            }
        });
    }, [product?.id, product?.isVariantParent, variantChildren, variantSelection]);

    // Find the variant child whose trait values match the current selection
    const selectedVariant: ProductModel | undefined = useMemo(() => {
        if (!product?.isVariantParent || !variantChildren || Object.keys(variantSelection).length === 0) {
            return undefined;
        }
        return variantChildren.find(child =>
            child.childTraitValues?.every(ctv => variantSelection[ctv.styleTraitId] === ctv.id),
        );
    }, [product?.id, product?.isVariantParent, variantChildren, variantSelection]);

    // Load real-time pricing for the selected variant using its own product ID
    // (variant parent IDs return $0 - actual pricing lives on the variant child)
    useEffect(() => {
        if (!selectedVariant) {
            return;
        }
        if (variantPricingByProductId?.[selectedVariant.id]) {
            return;
        }
        const uom = selectedVariant.unitOfMeasures?.find(u => u.isDefault)?.unitOfMeasure ?? "";
        loadRealTimePricing({
            productPriceParameters: [{ productId: selectedVariant.id, unitOfMeasure: uom, qtyOrdered: 1 }],
            onSuccess: realTimePricing => {
                appDispatch({ type: "XKeller/CompleteLoadVariantPricing", realTimePricing });
            },
            onError: () => {
                // Silently ignore errors (e.g. Eclipse returning Forbidden on dev environment)
            },
        });
    }, [selectedVariant?.id]);

    // Load real-time inventory for the selected variant (mirrors PDP/PLP LoadRealTimeInventory)
    useEffect(() => {
        if (!selectedVariant) {
            return;
        }
        if (variantInventoryByProductId?.[selectedVariant.id]) {
            return;
        }
        loadRealTimeInventory({
            productIds: [selectedVariant.id],
            onSuccess: realTimeInventory => {
                appDispatch({ type: "XKeller/CompleteLoadVariantInventory", realTimeInventory });
            },
            onError: () => {
                // Fall back to the parent's own already-loaded inventory result (re-tagged with the
                // variant's product id) so the card shows consistent availability messaging.
                if (productInfo?.inventory) {
                    appDispatch({
                        type: "XKeller/CompleteLoadVariantInventory",
                        realTimeInventory: {
                            realTimeInventoryResults: [{ ...productInfo.inventory, productId: selectedVariant.id }],
                        },
                    });
                }
            },
        });
    }, [selectedVariant?.id]);

    // XNG-Changes
    // if (!product || !productInfo) {
    //     return null;
    // }

    // Merge the selected variant's images/pricing/inventory into the product so the card shows the
    // preselected item's price/availability, mirroring ProductListProductContext. Parent id and
    // isVariantParent are preserved here; the Add to Cart button below swaps to the variant's own id.
    const effectiveProduct: ProductModel = selectedVariant
        ? {
              ...product,
              productTitle: selectedVariant.productTitle || product.productTitle,
              smallImagePath: selectedVariant.smallImagePath || product.smallImagePath,
              mediumImagePath: selectedVariant.mediumImagePath || product.mediumImagePath,
              largeImagePath: selectedVariant.largeImagePath || product.largeImagePath,
              images:
                  selectedVariant.images && selectedVariant.images.length > 0
                      ? selectedVariant.images
                      : product.images,
              unitOfMeasures: selectedVariant.unitOfMeasures || product.unitOfMeasures,
          }
        : product;

    const variantPricing = selectedVariant ? variantPricingByProductId?.[selectedVariant.id] : undefined;
    const variantInventory = selectedVariant ? variantInventoryByProductId?.[selectedVariant.id] : undefined;
    const variantUnitOfMeasure = selectedVariant
        ? selectedVariant.customerUnitOfMeasure ||
          selectedVariant.unitOfMeasures?.find(o => o.isDefault)?.unitOfMeasure ||
          productInfo?.unitOfMeasure
        : productInfo?.unitOfMeasure;

    const effectiveProductInfo = selectedVariant
        ? {
              ...(productInfo ?? {}),
              unitOfMeasure: variantUnitOfMeasure,
              pricing: variantPricing ?? productInfo?.pricing,
              inventory: variantInventory ?? productInfo?.inventory,
              productDetailPath: selectedVariant.productNumber
                  ? `${productInfo?.productDetailPath}?option=${encodeURIComponent(selectedVariant.productNumber)}`
                  : productInfo?.productDetailPath,
          }
        : productInfo;

    // XNG-Changes
    const productContext: ProductContextModel = {
        product: (effectiveProduct ?? {}) as ProductModel,
        productInfo: (effectiveProductInfo ?? {}) as ProductInfo,
        onUnitOfMeasureChanged: updateUnitOfMeasure,
        onQtyOrderedChanged: updateQuantity,
    };

    // Several sub-components (ProductOriginally, ProductContextAvailability, ProductAddToListLink,
    // Add to Cart) hide themselves whenever product.isVariantParent is true, so once a variant is
    // resolved this fully swaps the product/productInfo in (mirrors AddToCartActionsSection on PLP).
    const resolvedVariantContext: ProductContextModel | undefined = useMemo(() => {
        if (!selectedVariant) {
            return undefined;
        }
        return {
            product: selectedVariant,
            productInfo: {
                ...createFromProduct(selectedVariant),
                qtyOrdered: productInfo?.qtyOrdered ?? 1,
                unitOfMeasure: variantUnitOfMeasure ?? "",
                pricing: variantPricing ?? productInfo?.pricing,
                inventory: variantInventory ?? productInfo?.inventory,
            },
            onQtyOrderedChanged: updateQuantity,
            onUnitOfMeasureChanged: updateUnitOfMeasure,
        };
    }, [selectedVariant, variantPricing, variantInventory, variantUnitOfMeasure, productInfo?.qtyOrdered]);

    // Dispatch a manual variant trait change from the carousel card's own dropdown (mirrors PLP)
    const handleUpdateVariantSelection = (traitValueId: string, variantTraitId: string) => {
        manuallyChangedRef.current = true;
        appDispatch({
            type: "XKeller/UpdateProductListVariantSelection",
            parentProductId: product.id,
            variantTraitId,
            traitValueId,
        });
    };

    const sortedVariantTraits =
        product?.isVariantParent && product.variantTraits && product.variantTraits.length > 0
            ? product.variantTraits.slice().sort((a, b) => a.sortOrder - b.sortOrder)
            : [];

    const trackProductRecommendation = (isNewPageWasOpen: boolean) => {
        if (recommendationId) {
            sendProductRecommendationTracking({ recommendationId, isNewPageWasOpen });
        }
    };

    const {
        showOverlayBadge,
        overlayBadge,
        overlayBadgeWrapperStyles,
        badgeAreaWrapperRef,
        overlayBadgeWrapperClickHandler,
    } = useOverlayBadge({
        productContext,
        showTextBadge: showTextBadges,
        showImageBadge: showImageBadges,
        styles,
    });

    const history = useHistory();
    // Only fall back to "Choose Options" while a variant hasn't been resolved yet (still loading or
    // no in-stock child found); once selectedVariant is available, render the card like a regular product.
    const needsVariantSelection = !!product?.isVariantParent && !selectedVariant;
    const productDetailPath = productContext.productInfo.productDetailPath || productContext.product.uri;

    if (!product || !productInfo) {
        return null;
    }

    const { imageBadges, textBadges } = useWidgetBadges({ productContext, showTextBadges, showImageBadges });

    return (
        <>
            <ProductContext.Provider value={productContext}>
                <ProductGridLayout
                    id={`product_${product.id}`}
                    data-test-selector="product-carousel-item"
                    extendedStyles={styles.productGridLayoutStyle}
                >
                    <ProductGridLayout.Image>
                        {showImage && !showOverlayBadge && (
                            <ProductImage
                                product={productContext}
                                onClick={() => trackProductRecommendation(true)}
                                extendedStyles={styles.productImageStyles}
                            />
                        )}
                        {showImage && showOverlayBadge && overlayBadge && (
                            <StyledWrapper
                                {...styles.badgeAreaWrapper}
                                ref={badgeAreaWrapperRef}
                                data-test-selector="productCarouselBadge"
                            >
                                <ProductImage extendedStyles={styles.productImageStyles} product={productContext} />
                                <StyledWrapper {...overlayBadgeWrapperStyles} onClick={overlayBadgeWrapperClickHandler}>
                                    <Badge
                                        type={overlayBadge.badgeType}
                                        value={
                                            overlayBadge.badgeType === "Image"
                                                ? overlayBadge.largeImageBadgePath
                                                : overlayBadge.displayText
                                        }
                                        badgeTextColor={overlayBadge.textColorHexCode}
                                        badgeBackgroundColor={overlayBadge.badgeColorHexCode}
                                        badgeTextRounding={overlayBadge.badgeStyle === "Round" ? 20 : 0}
                                        imageAltText={
                                            overlayBadge.imageAltText ? overlayBadge.imageAltText : overlayBadge.name
                                        }
                                        textBadgeCss={styles.badgeText}
                                        imageBadgeProps={styles.badgeImageProps}
                                    />
                                </StyledWrapper>
                            </StyledWrapper>
                        )}
                    </ProductGridLayout.Image>
                    <ProductGridLayout.Info>
                        {showBrand && product.brand && (
                            <ProductBrand brand={product.brand} extendedStyles={styles.productBrandStyles} />
                        )}
                        {showTitle && (
                            <ProductDescription
                                product={productContext}
                                onClick={() => trackProductRecommendation(true)}
                                extendedStyles={styles.productDescriptionStyles}
                            />
                        )}
                        {showPartNumbers && (
                            // Show the preselected variant's own numbers once resolved, like ProductListProductInformation
                            <ProductPartNumbers
                                productNumber={selectedVariant?.productNumber || product.productNumber}
                                customerProductNumber={product.customerProductNumber}
                                manufacturerItem={selectedVariant?.manufacturerItem || product.manufacturerItem}
                                showCustomerName={false}
                                showManufacturerItem={showManufacturerItem}
                                extendedStyles={styles.productPartNumbersStyles}
                            />
                        )}
                        {sortedVariantTraits.map(variantTrait => (
                            <ProductDetailsVariantDropdown
                                key={variantTrait.id}
                                variantSelection={variantSelection}
                                variantTrait={variantTrait}
                                updateVariantSelection={handleUpdateVariantSelection}
                            />
                        ))}
                        {showTextBadges && textBadges && (
                            <ProductTextBadges badgeList={textBadges} maxBadges={maximumNumberOfTextBadges ?? 4} />
                        )}
                        {showImageBadges && imageBadges && (
                            <ProductImageBadges badgeList={imageBadges} maxBadges={maximumNumberOfImageBadges ?? 4} />
                        )}
                    </ProductGridLayout.Info>
                    <ProductGridLayout.PriceSection>
                        {resolvedVariantContext ? (
                            <ProductContext.Provider value={resolvedVariantContext}>
                                <ProductOriginally productContext={resolvedVariantContext} />
                            </ProductContext.Provider>
                        ) : (
                            <ProductOriginally productContext={productContext} />
                        )}
                        {showPrice && (
                            <StyledWrapper {...styles.productPriceWrapper}>
                                <VisuallyHidden as="label" htmlFor={`${carouselId}-uom`} id={`${carouselId}-uom-label`}>
                                    {translate("U/M")}
                                </VisuallyHidden>
                                <ProductPrice showLabel={false} product={productContext} />
                                {product.unitOfMeasures && (
                                    <ProductUnitOfMeasureSelect
                                        labelOverride=""
                                        extendedStyles={styles.productUnitOfMeasureSelectStyles}
                                        id={`${carouselId}-uom`}
                                    />
                                )}
                                <ProductQuantityBreakPricing
                                    extendedStyles={styles.productQuantityBreakPricingStyles}
                                />
                            </StyledWrapper>
                        )}
                        <ProductGridLayout.AvailabilitySection>
                            {showInventory &&
                                (resolvedVariantContext ? (
                                    <ProductContext.Provider value={resolvedVariantContext}>
                                        <ProductContextAvailability extendedStyles={styles.productAvailabilityStyles} />
                                    </ProductContext.Provider>
                                ) : (
                                    <ProductContextAvailability extendedStyles={styles.productAvailabilityStyles} />
                                ))}
                            <ProductContextPreviousPurchase />
                        </ProductGridLayout.AvailabilitySection>
                    </ProductGridLayout.PriceSection>
                    <ProductGridLayout.AddToCartSection>
                        {showAddToCart && isAuthenticated && !needsVariantSelection && (
                            <>
                                <VisuallyHidden
                                    as="label"
                                    htmlFor={`${carouselId}-qty-${product.id}`}
                                    id={`${carouselId}-qty-label`}
                                >
                                    {translate("QTY")}
                                </VisuallyHidden>
                                {resolvedVariantContext ? (
                                    // Provide the resolved variant so Add to Cart adds the actual item, not the parent style
                                    <ProductContext.Provider value={resolvedVariantContext}>
                                        <ProductQuantityOrdered
                                            labelOverride=""
                                            showActions={true}
                                            extendedStyles={styles.productQuantityOrderedStyles}
                                            id={`${carouselId}-qty-${product.id}`}
                                        />
                                        <ProductAddToCartButton
                                            onAddToCartClick={() => trackProductRecommendation(false)}
                                            data-test-selector={`addToCartBtn${selectedVariant!.id}`}
                                            extendedStyles={styles.productAddToCartButtonStyles}
                                            variantSelectionCompleted={true}
                                        />
                                    </ProductContext.Provider>
                                ) : (
                                    <>
                                        <ProductQuantityOrdered
                                            labelOverride=""
                                            showActions={true}
                                            extendedStyles={styles.productQuantityOrderedStyles}
                                            id={`${carouselId}-qty-${product.id}`}
                                        />
                                        <ProductAddToCartButton
                                            onAddToCartClick={() => trackProductRecommendation(false)}
                                            data-test-selector="addToCartBtn"
                                            extendedStyles={styles.productAddToCartButtonStyles}
                                        />
                                    </>
                                )}
                            </>
                        )}
                        {showAddToCart && !isAuthenticated && (
                            <>
                                <SignInButton extendedStyles={styles.signInButton} />
                            </>
                        )}
                    </ProductGridLayout.AddToCartSection>
                    <ProductGridLayout.AddToListAction>
                        <ProductContext.Provider value={resolvedVariantContext ?? productContext}>
                            {showAddToList && (
                                <ProductAddToListLink
                                    data-test-selector="addToListLink"
                                    extendedStyles={styles.productAddToListLinkStyles}
                                />
                            )}
                            {/* Xng-changes : add ProductAddToListLink for web-only with button style */}
                            {isNonPurchasable && (
                                <ProductAddToListLink
                                    data-test-selector="addToListLink"
                                    extendedStyles={styles.productAddToListLinkStyles}
                                    showAsButton={isNonPurchasable}
                                />
                            )}
                        </ProductContext.Provider>
                        {actionsTemplate ? actionsTemplate(product) : []}
                    </ProductGridLayout.AddToListAction>

                    <ProductGridLayout.AddToCartVariantParent>
                        {showAddToCart && isAuthenticated && needsVariantSelection && (
                            <>
                                <AddToCartVariantParent
                                    {...(isNonPurchasable
                                        ? styles.addToCartVariantParentNonPurchasable
                                        : styles.addToCartVariantParent)}
                                    onClick={() => history.push(productDetailPath)}
                                />
                                {!isNonPurchasable && <StyledWrapper {...styles.emptyElement}></StyledWrapper>}
                            </>
                        )}
                    </ProductGridLayout.AddToCartVariantParent>
                </ProductGridLayout>
            </ProductContext.Provider>
        </>
    );
};

ProductCarouselProduct.defaultProps = {
    showInventory: true,
    showManufacturerItem: true,
};

export default connect(mapStateToProps, mapDispatchToProps)(ProductCarouselProduct);
